import { NeoModel, Validation } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { AppService, Types } from '../../AwardsTypes';
import { Types as CommonAwardTypes } from 'st-common/Awards/CommonAwardsTypes';
import SurveyParticipantDocumentDetailedLookup from 'st-common/Awards/Models/Surveys/Queries/SurveyParticipantDocumentDetailedLookup';
import SurveyParticipantLookup from 'st-common/Awards/Models/Surveys/Queries/SurveyParticipantLookup';
import SurveyParticipantDocumentLookup from 'st-common/Awards/Models/Surveys/Queries/SurveyParticipantDocumentLookup';
import { AwardDocumentComponentVM } from '../Acceptance/Components/Steps/Documents/AwardDocumentComponent';
import ParticipantOfferAwardDocumentLookup from 'st-common/Awards/Models/AwardDocuments/Queries/ParticipantOfferAwardDocumentLookup';
import { AwardDocumentAcceptanceType } from 'st-common/Awards/Models/AwardDocuments/AwardDocumentAcceptanceType.enum';
import { NotificationDuration } from 'st-app/Models/Enums/NotificationDuration';
import { SurveyQuestionnaireComponentComponentVM } from './SurveyQuestionnaireComponent';
import Home from 'st-app/Views/Home';

export enum SurveyViewMode {
    Survey = "survey",
    Document = "document",
    Questionnaire = "questionnaire"
}

interface ISurveyStep { mode: SurveyViewMode, surveyParticipantDocumentId?: number }
interface ILoadRequest { mode: SurveyViewMode, surveyParticipantDocumentId?: number }

@NeoModel
export default class SurveyCompletionVM extends Views.ViewModelBase {

    public documentComponentVM: AwardDocumentComponentVM;

    constructor(taskRunner = AppService.get(Types.Neo.TaskRunner),
        private surveysApiClient = AppService.get(CommonAwardTypes.ApiClients.SurveysApiClient),
        private navigation = AppService.get(Types.Neo.Routing.NavigationHelper),
        public notifications = AppService.get(Types.Neo.UI.GlobalNotifications)) {
        super(taskRunner);
        this.documentComponentVM = new AwardDocumentComponentVM(
            this.taskRunner,
            this.documentCompleted.bind(this) as (document: ParticipantOfferAwardDocumentLookup | SurveyParticipantDocumentDetailedLookup) => void

        );
        this.questionnaireComponentVM = this.registerViewModel(new SurveyQuestionnaireComponentComponentVM(this.taskRunner));
    }

    public stepIndex = 0;
    public surveySteps: ISurveyStep[] = [];
    public viewMode = SurveyViewMode.Survey;
    public surveyParticipantDocumentLookup = new SurveyParticipantDocumentDetailedLookup();
    public surveyParticipantLookup = new SurveyParticipantLookup();
    public isLastDocument = false;
    public questionnaireComponentVM: SurveyQuestionnaireComponentComponentVM;

    public async initialise() {
        this.stepIndex = 0;
    }

    private documentCompleted(document: SurveyParticipantDocumentDetailedLookup) {
        this.surveyParticipantDocumentLookup.acceptedTermsAndConditions = document.acceptedTermsAndConditions;
        this.surveyParticipantDocumentLookup.respondedOn = new Date();
        this.notifications.addSuccess("Response saved", null, NotificationDuration.Standard);
        this.onDocumentCompleted(document);
    }

    private onDocumentCompleted(document: SurveyParticipantDocumentDetailedLookup) {
        const surveyDoc = this.surveyParticipantLookup.documents.find(c => c.surveyParticipantDocumentId === document.surveyParticipantDocumentId);
        if (surveyDoc) {
            surveyDoc.respondedOn = document.respondedOn;
            surveyDoc.uploadedFileName = document.uploadedFileName;
        }
    }

    public async loadSurveyData(surveyParticipantId: number = 0) {
        await this.taskRunner.run(async () => {
            await this.loadSurveyParticipantData(surveyParticipantId);
        });

        this.startSurvey();
    }

    public async loadSurveyParticipantDocument(surveyParticipantId: number = 0, surveyParticipantDocumentId: number = 0) {
        this.documentComponentVM.disposeReactions();

        const result = await this.surveysApiClient.getSurveyParticipantDocumentLookup(surveyParticipantId, surveyParticipantDocumentId);
        this.surveyParticipantDocumentLookup.set(result.data);

        if (this.surveyParticipantDocumentLookup.firstViewedOn == null) {
            await this.surveysApiClient.updateSurveyParticipantDates(surveyParticipantId, true, false);
        }
    }

    public async loadSurveyParticipantData(surveyParticipantId: number) {
        const result = await this.surveysApiClient.getSurveyParticipantLookupAsync(surveyParticipantId);
        this.surveyParticipantLookup = SurveyParticipantLookup.fromJSObject<SurveyParticipantLookup>(result.data);
    }

    private startSurvey() {
        this.setupSurveySteps();
        this.stepIndex = 0;
        this.loadSurveySteps();
    }

    private setupSurveySteps() {
        const surveySteps: ISurveyStep[] = [];
        surveySteps.push({ mode: SurveyViewMode.Survey });

        for (const surveyParticipantDocumentLookup of this.surveyParticipantLookup.documents) {
            surveySteps.push({ mode: SurveyViewMode.Document, surveyParticipantDocumentId: surveyParticipantDocumentLookup.surveyParticipantDocumentId });
        }

        if (this.surveyParticipantLookup.questionnaireId) {
            surveySteps.push({ mode: SurveyViewMode.Questionnaire });
        }

        this.surveySteps = surveySteps;
    }

    public async next() {
        if (await this.canLeaveStep()) {
            this.stepIndex++;
            await this.loadSurveySteps();
        }
    }

    public async startQuestionnaire() {
        await this.next();
    }

    private async saveQuestionnaireAnswers() {
        if (this.questionnaireComponentVM.updateQuestionnaireAnswerCommand.isValid) {
            await this.questionnaireComponentVM.saveQuestionnaireAnswers()
            return true;
        } else {
            this.questionnaireComponentVM.validationDisplayMode = Validation.DisplayMode.Always;
            this.questionnaireComponentVM.updateQuestionnaireAnswerCommand.validator.forceShowErrors = true;
            return false;
        }
    }

    public async completeSurvey() {

        if (this.viewMode === SurveyViewMode.Questionnaire) {
            await this.saveQuestionnaireAnswers();
        }

        if (await this.canLeaveStep()) {
            await this.surveysApiClient.updateSurveyParticipantDates(this.surveyParticipantLookup.surveyParticipantId, false, true);
            this.notifications.addInfo("Thank you for completing this task!", null, NotificationDuration.Standard);

            return true;
        }

        return false;
    }

    public async back() {
        if (this.stepIndex > 0) {
            this.stepIndex--;
            await this.loadSurveySteps();
            this.checkLastDocument();
        }
        else {
            this.navigation.navigateToView(Home);
        }
    }

    private checkLastDocument() {
        if (this.stepIndex === this.surveySteps.filter(x => x.mode == SurveyViewMode.Document).length) {
            this.isLastDocument = true;
        }
    }

    private async canLeaveStep() {
        if (this.viewMode === SurveyViewMode.Document) {
            if (!this.documentComponentVM.document.isCompleted) {
                if (this.documentComponentVM.document.acceptanceType === AwardDocumentAcceptanceType.AcceptTermsAndConditions) {
                    this.notifications.addWarning("Please accept the terms and conditions", null, NotificationDuration.Standard);
                } else {
                    this.notifications.addWarning("Please upload required document", null, NotificationDuration.Standard);
                }
                return false;
            }
        }
        else if (this.viewMode === SurveyViewMode.Questionnaire) {
            if (!this.questionnaireComponentVM.updateQuestionnaireAnswerCommand.isValid) {
                return false;
            }
        }

        return true;

    }

    private async loadSurveySteps() {
        const step = this.surveySteps[this.stepIndex];
        await this.loadSurveyViewMode({ mode: step.mode, surveyParticipantDocumentId: step.surveyParticipantDocumentId });
    }

    private async loadSurveyViewMode(request: ILoadRequest) {
        if (request.mode === SurveyViewMode.Survey) {
            this.viewMode = request.mode;
            this.loadSurveyParticipantDocument(this.surveyParticipantLookup.surveyParticipantId);
            return true;
        }
        else if (request.mode === SurveyViewMode.Document) {
            this.checkLastDocument();
            const document = await this.loadSurveyDocument(this.surveyParticipantLookup, request);

            if (document) {
                this.surveyParticipantDocumentLookup = document;
                this.viewMode = request.mode;
                return true;
            }
        }
        else if (request.mode === SurveyViewMode.Questionnaire) {
            this.viewMode = request.mode;
            await this.questionnaireComponentVM.loadSurveyParticipantQuestionnaireAnswers(this.surveyParticipantLookup.surveyParticipantId, this.surveyParticipantLookup.questionnaireId!, this.surveyParticipantLookup.questionnaireAnsweredId);
            return true;
        }
        return false;
    }

    public async loadSurveyDocument(survey: SurveyParticipantLookup, request: ILoadRequest) {
        if (survey.documents && survey.documents.length > 0) {
            let surveyDocument: SurveyParticipantDocumentLookup | null = null;
            if (request.surveyParticipantDocumentId) {
                surveyDocument = survey.documents.find(c => c.surveyParticipantDocumentId === request.surveyParticipantDocumentId) ?? null;
                if (surveyDocument) {
                    const result = await this.surveysApiClient.getSurveyParticipantDocumentLookup(0, surveyDocument.surveyParticipantDocumentId);
                    const document = SurveyParticipantDocumentDetailedLookup.fromJSObject<SurveyParticipantDocumentDetailedLookup>(result.data);
                    return this.setupDocument(document);
                }
            }
        }
        return null;
    }

    private setupDocument(document: SurveyParticipantDocumentDetailedLookup) {
        if (document) {
            this.documentComponentVM.disposeReactions();
            this.documentComponentVM.document = document;
            this.documentComponentVM.setup();
        }

        return document;
    }
}
